import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";

/**
 * How long a list the API answers is counted fresh. Short on purpose: every one of these lists is fed by something
 * that writes after the request that started it was answered — the Directory's projector, the audit queue's consumer,
 * the Intake copier — so a page that is opened again, or a window that is come back to, asks rather than shows what it
 * had. It is not zero: the sidebar and a page often ask for the same list within a render or two.
 */
export const LIST_STALE_TIME_MS = 5000;

/**
 * The waits, counted from the moment a write is answered, at which a list one of those writers feeds is asked again.
 * The immediate ask is normally too early — the projector has not run and the audit message is still in the queue —
 * and these two are what catch up. A list nothing is showing is only marked stale by them, so they cost nothing for a
 * page that is not open; it asks the moment it is opened.
 */
export const SETTLE_DELAYS_MS = [1500, 4000] as const;

/**
 * Asks lists again after a write the API answers before the change has reached them: now, and again after each of
 * `SETTLE_DELAYS_MS`. The promise it answers is the immediate round alone, so a caller can await it the way it awaited
 * an `invalidateQueries`.
 *
 * The waits are deliberately not tied to the component that starts them, and nothing cancels them. Every one of these
 * writes is followed by the page going somewhere else — the Add party drawer lands on the new record, a removal leaves
 * the page it removed — so a wait cancelled on unmount would be cancelled exactly when it was about to be worth
 * something. Invalidating a list nothing is showing only marks it stale, which costs no request; it is what makes the
 * list right the moment somebody opens it again.
 */
async function askAgain(queryClient: QueryClient, queryKeys: readonly QueryKey[]): Promise<void> {
	const ask = () => Promise.all(queryKeys.map(queryKey => queryClient.invalidateQueries({ queryKey })));
	// Scheduled before the immediate round is awaited, so the waits are counted from the write rather than from the
	// refetch the write starts.
	for (const delay of SETTLE_DELAYS_MS) {
		setTimeout(() => {
			// A later round has nowhere to show a failure; the query keeps its own error state.
			void ask().catch(() => undefined);
		}, delay);
	}
	await ask();
}

/** `askAgain` bound to the page's query client, which is how every page reaches it. */
export function useAskAgain(): (...queryKeys: QueryKey[]) => Promise<void> {
	const queryClient = useQueryClient();
	return (...queryKeys) => askAgain(queryClient, queryKeys);
}
